/* Legacy CS2 teams keep their existing management controls. */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store';
import { Ic } from '../icons';
import { Pill } from '../ui';
const ROLE_LABEL = { igl: 'IGL', awp: 'AWP', entry: 'Entry', support: 'Support', lurk: 'Lurk' };

function Spin({ size = 18 }) {
  return (
    <span
      className="inline-block flex-shrink-0"
      style={{
        width: size, height: size, borderRadius: '50%',
        border: '2px solid rgba(255,255,255,.15)', borderTopColor: 'var(--fed-accent-bright)',
        animation: 'fedSpin .7s linear infinite',
      }}
    />
  );
}

/** Карточка-панель витрины */
function Card({ children, className = '', style }) {
  return (
    <div className={`rounded-2xl bg-surface border border-line ${className}`} style={{ padding: 26, ...style }}>
      {children}
    </div>
  );
}

/** Моно-заголовок блока внутри карточки */
function BlockLabel({ children, color = 'var(--fed-fg-muted)', style }) {
  return (
    <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '.2em', color, ...style }}>
      {children}
    </div>
  );
}

/** Квадратный аватар-плашка с инициалами/тегом */
function Ava({ text, size = 40, radius = 11, accent = false, glowColor }) {
  return (
    <div
      className="flex-shrink-0 flex items-center justify-center font-display font-bold text-fg"
      style={{
        width: size, height: size, borderRadius: radius, fontSize: Math.round(size / 2.9),
        background: accent
          ? 'linear-gradient(140deg,var(--fed-accent-deep),var(--fed-surface-3))'
          : 'linear-gradient(150deg,var(--fed-surface-3),var(--fed-elevated))',
        border: `1px solid ${accent ? 'var(--fed-line-accent)' : 'var(--fed-line)'}`,
        boxShadow: glowColor ? `0 0 26px -8px ${glowColor}` : undefined,
      }}
    >
      {text}
    </div>
  );
}

function JoinRequestsPanel({ teamId }) {
  const { fetchTeamRequests, respondToJoinRequest } = useAppStore();
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const data = await fetchTeamRequests(teamId);
      if (alive) { setRequests(data || []); setLoading(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const respond = async (reqId, action) => {
    setBusy(reqId);
    try {
      await respondToJoinRequest(reqId, action);
      setRequests((rs) => rs.filter((r) => r.id !== reqId));
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="flex justify-center" style={{ padding: 20 }}><Spin size={20} /></div>;
  if (requests.length === 0) {
    return (
      <div className="text-center text-fg-muted" style={{ padding: '12px 0', fontSize: 13.5 }}>
        {t('profile.noRequests')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {requests.map((r) => (
        <div
          key={r.id}
          className="flex items-center gap-3 rounded-xl border border-line"
          style={{ padding: '11px 13px', background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}
        >
          <Ava text={(r.user?.nickname || '?').slice(0, 2).toUpperCase()} size={38} radius={10} />
          <div className="flex flex-col min-w-0 flex-1">
            <span className="font-semibold text-fg truncate" style={{ fontSize: 14 }}>{r.user?.nickname || '—'}</span>
            {r.user?.full_name && <span className="text-fg-muted truncate" style={{ fontSize: 12, marginTop: 1 }}>{r.user.full_name}</span>}
          </div>
          <button
            onClick={() => respond(r.id, 'accept')}
            disabled={busy === r.id}
            className="flex-shrink-0 h-9 px-3.5 rounded-[9px] cursor-pointer font-display font-bold uppercase"
            style={{ fontSize: 12, letterSpacing: '.04em', border: '1px solid rgba(67,217,106,.4)', background: 'rgba(67,217,106,.1)', color: 'var(--fed-ok)' }}
          >
            {busy === r.id ? <Spin size={13} /> : t('profile.accept')}
          </button>
          <button
            onClick={() => respond(r.id, 'reject')}
            disabled={busy === r.id}
            className="fed-btn-ghost flex-shrink-0 h-9 px-3.5 rounded-[9px]"
            style={{ fontSize: 12 }}
          >
            {t('profile.decline')}
          </button>
        </div>
      ))}
    </div>
  );
}

/** Карточка команды: нет команды / заявка на рассмотрении / состав */
export default function LegacyTeamPanel({ u, team }) {
  const { removeTeamMember, deleteTeam, leaveTeam } = useAppStore();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [kickBusy, setKickBusy] = useState(null);
  const [confirmDissolve, setConfirmDissolve] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const td = u.team_data;
  const isCaptain = td?.captain_id === u.id;
  const members = td?.members || [];

  const copyCode = () => {
    navigator.clipboard?.writeText(td?.join_code || '').catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const kick = async (userId) => {
    setKickBusy(userId);
    try { await removeTeamMember(td.id, userId); } catch (e) {}
    setKickBusy(null);
  };

  const dissolve = async () => {
    if (!confirmDissolve) { setConfirmDissolve(true); return; }
    setActionBusy(true);
    try { await deleteTeam(td.id); } catch (e) {}
    setActionBusy(false); setConfirmDissolve(false);
  };

  const leave = async () => {
    if (!confirmLeave) { setConfirmLeave(true); return; }
    setActionBusy(true);
    try { await leaveTeam(td.id); } catch (e) {}
    setActionBusy(false); setConfirmLeave(false);
  };

  const head = (
    <h3 style={{ fontSize: 20 }}>{t('profile.myTeam')}</h3>
  );

  // === заявка в команду отправлена, ждём ответа капитана ===
  if (!team && u.pending_request) {
    return (
      <Card>
        <div style={{ marginBottom: 16 }}>{head}</div>
        <div
          className="flex gap-3 items-center rounded-[13px]"
          style={{ padding: 16, background: 'rgba(59,165,217,.07)', border: '1px solid rgba(59,165,217,.3)' }}
        >
          <span className="flex-shrink-0" style={{ color: 'var(--fed-info)' }}><Ic name="info" size={22} /></span>
          <div>
            <div className="font-semibold text-fg" style={{ fontSize: 14.5 }}>{t('profile.pendingRequest')}</div>
            <div className="text-fg-muted" style={{ fontSize: 13, marginTop: 2 }}>
              {t('profile.pendingRequestSub')}{' '}
              <strong className="text-fg">«{u.pending_request.team?.name || '—'}»</strong>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // === команды нет ===
  if (!team) {
    return (
      <Card>
        <div style={{ marginBottom: 16 }}>{head}</div>
        <div
          className="flex gap-3 items-center rounded-[13px]"
          style={{ padding: 16, background: 'rgba(230,183,60,.07)', border: '1px solid rgba(230,183,60,.3)' }}
        >
          <span className="flex-shrink-0 text-gold"><Ic name="info" size={22} /></span>
          <div>
            <div className="font-semibold text-fg" style={{ fontSize: 14.5 }}>{t('profile.noTeam')}</div>
            <div className="text-fg-muted" style={{ fontSize: 13, marginTop: 2 }}>{t('profile.noTeamSub')}</div>
          </div>
        </div>
      </Card>
    );
  }

  // === команда есть ===
  const teamStatus = td?.status === 'full'
    ? { l: t('profile.fullTeam'), c: 'var(--fed-accent-bright)' }
    : td?.status === 'moderation'
      ? { l: t('profile.teamOnModeration'), c: 'var(--fed-info)' }
      : { l: t('profile.recruiting'), c: 'var(--fed-ok)' };

  return (
    <Card style={{ padding: 0 }} className="overflow-hidden">
      {/* шапка команды */}
      <div className="relative overflow-hidden" style={{ padding: 26, borderBottom: '1px solid var(--fed-line)' }}>
        <div className="absolute inset-0" style={{ opacity: 0.5, background: 'radial-gradient(ellipse at 92% -30%,rgba(225,18,23,.22),transparent 60%)' }} />
        <div className="relative flex justify-between items-center flex-wrap gap-2" style={{ marginBottom: 18 }}>
          {head}
          <div className="flex gap-2 flex-wrap">
            <Pill color={isCaptain ? 'var(--fed-gold)' : 'var(--fed-ok)'}>
              {isCaptain ? t('common.captain') : t('profile.inTeam')}
            </Pill>
            <Pill color={teamStatus.c} dot>{teamStatus.l}</Pill>
          </div>
        </div>
        <div className="relative flex gap-4 items-center">
          <Ava text={(td?.tag || team).slice(0, 3).toUpperCase()} size={58} radius={14} accent glowColor="var(--fed-accent-glow)" />
          <div className="flex flex-col gap-1.5 min-w-0">
            <span className="font-display font-bold text-fg uppercase" style={{ fontSize: 22, lineHeight: 1.05 }}>{team}</span>
            {isCaptain && td?.join_code && (
              <div className="flex gap-2 items-center flex-wrap">
                <span className="font-mono uppercase text-fg-faint" style={{ fontSize: 9.5, letterSpacing: '.12em' }}>
                  {t('profile.joinCode')}
                </span>
                <span className="font-mono font-bold text-gold" style={{ fontSize: 13, letterSpacing: '.14em' }}>{td.join_code}</span>
                <button
                  onClick={copyCode}
                  className="cursor-pointer font-mono rounded-md transition-colors"
                  style={{
                    padding: '3px 9px', fontSize: 10.5, letterSpacing: '.06em',
                    border: `1px solid ${copied ? 'rgba(67,217,106,.45)' : 'var(--fed-line-2)'}`,
                    background: copied ? 'rgba(67,217,106,.1)' : 'var(--fed-surface-2)',
                    color: copied ? 'var(--fed-ok)' : 'var(--fed-fg-muted)',
                  }}
                >
                  {copied ? t('profile.codeCopied') : t('profile.copyCode')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* состав */}
      <div style={{ padding: 26 }}>
        <BlockLabel style={{ marginBottom: 12 }}>{t('profile.roster')} · {members.length}/5</BlockLabel>
        {members.length === 0 ? (
          <div className="text-fg-faint" style={{ fontSize: 13, padding: '8px 0' }}>{t('profile.noMembers')}</div>
        ) : (
          <div className="flex flex-col gap-2">
            {members.map((m) => {
              const isMe = m.user_id === u.id;
              return (
                <div
                  key={m.id}
                  className="flex items-center gap-3 rounded-xl"
                  style={{
                    padding: '10px 12px',
                    background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))',
                    border: `1px solid ${isMe ? 'var(--fed-line-accent)' : 'var(--fed-line)'}`,
                  }}
                >
                  <Ava text={(m.user?.nickname || '?').slice(0, 2).toUpperCase()} size={36} radius={10} accent={isMe} />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="font-semibold text-fg truncate" style={{ fontSize: 14 }}>{m.user?.nickname || '—'}</span>
                    {m.user?.full_name && <span className="text-fg-muted truncate" style={{ fontSize: 11.5, marginTop: 1 }}>{m.user.full_name}</span>}
                  </div>
                  {td?.captain_id === m.user_id && (
                    <span className="flex-shrink-0 font-mono uppercase text-gold" style={{ fontSize: 9, letterSpacing: '.1em' }}>
                      {t('common.captain')}
                    </span>
                  )}
                  {isMe && (
                    <span className="flex-shrink-0 font-mono uppercase" style={{ fontSize: 9, letterSpacing: '.1em', color: 'var(--fed-info)' }}>
                      {t('profile.youLabel')}
                    </span>
                  )}
                  <span
                    className="flex-shrink-0 font-mono uppercase rounded-full border border-line bg-surface-2 text-fg-muted"
                    style={{ fontSize: 9.5, letterSpacing: '.08em', padding: '3px 9px' }}
                  >
                    {ROLE_LABEL[m.role] || m.role || '—'}
                  </span>
                  {isCaptain && !isMe && (
                    <button
                      onClick={() => kick(m.user_id)}
                      disabled={kickBusy === m.user_id}
                      title={t('profile.kickPlayer')}
                      className="fed-iconbtn"
                      style={{ width: 28, height: 28, borderRadius: 8 }}
                    >
                      {kickBusy === m.user_id ? <Spin size={12} /> : <Ic name="x" size={12} sw={2.4} />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* заявки в команду */}
        {isCaptain && (
          <div style={{ marginTop: 22 }}>
            <BlockLabel color="var(--fed-gold)" style={{ marginBottom: 12 }}>{t('profile.joinRequests')}</BlockLabel>
            <JoinRequestsPanel teamId={td.id} />
          </div>
        )}

        {/* распустить / выйти */}
        <div className="flex gap-2.5 items-center flex-wrap" style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--fed-line)' }}>
          <button
            onClick={isCaptain ? dissolve : leave}
            disabled={actionBusy}
            className="h-10 px-4 gap-2 rounded-[10px] cursor-pointer font-display font-bold uppercase transition-colors"
            style={{
              fontSize: 12.5, letterSpacing: '.04em',
              border: `1px solid ${(isCaptain ? confirmDissolve : confirmLeave) ? (isCaptain ? 'var(--fed-accent)' : 'rgba(230,183,60,.55)') : 'var(--fed-line-2)'}`,
              background: (isCaptain ? confirmDissolve : confirmLeave) ? (isCaptain ? 'rgba(225,18,23,.1)' : 'rgba(230,183,60,.08)') : 'transparent',
              color: (isCaptain ? confirmDissolve : confirmLeave) ? (isCaptain ? 'var(--fed-accent-bright)' : 'var(--fed-gold)') : 'var(--fed-fg-muted)',
            }}
          >
            {actionBusy ? <Spin size={14} /> : <Ic name={isCaptain ? 'trash' : 'logout'} size={14} sw={2} />}
            {isCaptain
              ? (confirmDissolve ? t('profile.confirmDissolve') : t('profile.dissolveTeam'))
              : (confirmLeave ? t('profile.confirmLeave') : t('profile.leaveTeam'))}
          </button>
          {(isCaptain ? confirmDissolve : confirmLeave) && (
            <button
              onClick={() => (isCaptain ? setConfirmDissolve(false) : setConfirmLeave(false))}
              className="fed-btn-ghost h-10 px-4 rounded-[10px]"
              style={{ fontSize: 12.5 }}
            >
              {t('common.cancel')}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}
